import { Link } from 'react-router-dom';
import { ArrowRight, Baby, CalendarHeart, CheckCircle2, Gift, HeartHandshake, Palette, PawPrint, Sparkles, Users } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const occasions = [
  {
    icon: CalendarHeart,
    title: 'Noël, anniversaire ou fête des mères',
    text: 'Un tableau photo personnalisé fonctionne quand le cadeau doit être intime, durable et visible au quotidien.',
    to: '/cadeau-anniversaire-photo-personnalise',
    cta: 'Préparer un anniversaire',
  },
  {
    icon: HeartHandshake,
    title: 'Mariage, couple ou anniversaire de rencontre',
    text: 'Transformez une photo de couple, un lieu de cérémonie ou un souvenir partagé en décoration murale émotionnelle.',
    to: '/blog/cadeau-mariage-original',
    cta: 'Voir les idées mariage',
  },
  {
    icon: Users,
    title: 'Parents et grands-parents',
    text: 'Les photos de famille multi-générationnelles sont idéales pour offrir un objet qui raconte une histoire familiale.',
    to: '/cadeau-grands-parents-photo-personnalise',
    cta: 'Trouver une idée famille',
  },
  {
    icon: PawPrint,
    title: 'Amoureux des chiens et chats',
    text: 'Un portrait animalier personnalisé est une idée cadeau très forte pour les personnes attachées à leur compagnon.',
    to: '/portrait-animal-personnalise',
    cta: 'Créer un portrait animal',
  },
];

const giftIdeas = [
  {
    title: 'Portrait de famille en peinture à l’huile',
    bestFor: 'parents, grands-parents, salon classique',
    style: 'Peinture à l’huile',
    to: '/styles/oil-painting',
  },
  {
    title: 'Photo de couple en aquarelle',
    bestFor: 'mariage, Saint-Valentin, anniversaire de rencontre',
    style: 'Aquarelle',
    to: '/styles/watercolor',
  },
  {
    title: 'Portrait de chien ou chat en pop art',
    bestFor: 'cadeau drôle, moderne et très personnel',
    style: 'Pop art',
    to: '/styles/pop-art',
  },
  {
    title: 'Souvenir de voyage en croquis',
    bestFor: 'ami proche, décoration sobre, bureau',
    style: 'Croquis',
    to: '/styles/sketch',
  },
  {
    title: 'Photo d’enfant en rendu doux',
    bestFor: 'naissance, baptême, fête des mères',
    style: 'Aquarelle ou animation 3D',
    to: '/styles/anime',
  },
  {
    title: 'Photo de lieu de vie en tableau décoratif',
    bestFor: 'pendaison de crémaillère, maison familiale',
    style: 'Impressionniste',
    to: '/styles/impressionist',
  },
];

const recipientRoutes = [
  {
    title: 'Pour des grands-parents',
    context: 'Photo de famille, petits-enfants, moment de vacances ou souvenir multi-générationnel.',
    style: 'Aquarelle ou peinture à l’huile pour un rendu chaleureux et lisible.',
    link: '/cadeau-grands-parents-photo-personnalise',
    source: 'grandparents_route',
  },
  {
    title: 'Pour un mariage ou un couple',
    context: 'Photo de cérémonie, portrait de couple, lieu symbolique ou souvenir d’anniversaire.',
    style: 'Huile pour un rendu premium, aquarelle pour une ambiance douce et romantique.',
    link: '/blog/cadeau-mariage-original',
    source: 'couple_wedding_route',
  },
  {
    title: 'Pour un amoureux des animaux',
    context: 'Portrait de chien, chat ou compagnon avec regard net et sujet bien centré.',
    style: 'Croquis, pop art ou aquarelle selon la personnalité de l’animal.',
    link: '/portrait-animal-personnalise',
    source: 'pet_owner_route',
  },
  {
    title: 'Pour une occasion saisonnière',
    context: 'Noël, fête des mères, anniversaire ou pendaison de crémaillère avec une photo déjà chargée d’histoire.',
    style: 'Choisir d’abord l’émotion de la photo, puis un style polyvalent si vous hésitez.',
    link: '/cadeau-anniversaire-photo-personnalise',
    source: 'seasonal_route',
  },
];

const photoChecklist = [
  'Choisissez une photo nette, lumineuse et chargée émotionnellement pour le destinataire.',
  'Préférez un sujet principal lisible : visage, couple, famille, animal ou lieu souvenir.',
  'Évitez les images très sombres, floues ou avec des visages trop petits si le cadeau doit être un portrait.',
  'Si vous hésitez, commencez avec un style polyvalent comme aquarelle, peinture à l’huile ou croquis.',
];

const relatedPages = [
  { to: '/tableau-photo-personnalise', label: 'Tableau photo personnalisé', helper: 'La page produit pour comprendre le parcours photo → tableau.' },
  { to: '/portrait-famille-personnalise', label: 'Portrait de famille personnalisé', helper: 'Conseils de cadrage et de styles pour couples, fratries et familles.' },
  { to: '/cadeau-anniversaire-photo-personnalise', label: 'Cadeau anniversaire photo', helper: 'Un guide dédié aux anniversaires avec choix photo, styles et destinataires.' },
  { to: '/cadeau-grands-parents-photo-personnalise', label: 'Cadeau photo grands-parents', helper: 'Choisir une photo de famille et un style qui valorisent leur histoire.' },
  { to: '/portrait-animal-personnalise', label: 'Portrait animal personnalisé', helper: 'Conseils et styles pour transformer une photo de chien ou chat en tableau.' },
  { to: '/blog/idees-cadeaux-photo-personnalise', label: '10 idées cadeaux photo', helper: 'Le guide blog historique avec des exemples détaillés.' },
];

const createPath = (source: string) => `/create?source=gift_photo_${source}`;

const trackGiftCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'idees-cadeaux-photo-personnalises',
    source: `gift_photo_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/idees-cadeaux-photo-personnalises'),
      name: 'Idées cadeaux photo personnalisés',
      description:
        'Hub d’idées cadeaux photo personnalisés pour choisir une photo, un style artistique et une occasion avant de créer un tableau personnalisé.',
      url: absoluteUrl('/idees-cadeaux-photo-personnalises'),
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: absoluteUrl(),
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Accueil',
          item: absoluteUrl(),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Idées cadeaux photo personnalisés',
          item: absoluteUrl('/idees-cadeaux-photo-personnalises'),
        },
      ],
    },
  ],
};

const IdeesCadeauxPhotoPersonnalises = () => {
  return (
    <>
      <SEOHead
        title="Idées cadeaux photo personnalisés — Offrir un tableau unique | Ma Photo en Tableau"
        description="Trouvez une idée cadeau photo personnalisé pour Noël, mariage, fête des mères, grands-parents ou amoureux des animaux. Conseils photo, styles artistiques et CTA création."
        canonical={absoluteUrl('/idees-cadeaux-photo-personnalises')}
        schema={schema}
      />
      <div className="min-h-screen overflow-x-hidden">
        <main>
          <section className="section-shell gradient-hero relative overflow-hidden pt-32 pb-20">
            <div className="hero-orb left-[7%] top-28 h-32 w-32 bg-accent/40" />
            <div className="hero-orb right-[10%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
            <div className="container relative mx-auto px-6">
              <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                    Idées cadeaux photo personnalisés
                  </p>
                  <h1 className="display-title mb-6 text-foreground">
                    Trouvez le <span className="text-premium">cadeau photo personnalisé</span> qui fera vraiment plaisir.
                  </h1>
                  <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                    Pour un proche, un couple, des grands-parents ou un amoureux des animaux : partez d’une photo qui compte, choisissez un style artistique, puis créez un tableau personnalisé prêt à offrir.
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                      <Link to={createPath('hero_primary')} onClick={() => trackGiftCta('hero_primary')}>
                        Créer mon cadeau photo
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                      <Link to="/tableau-photo-personnalise">Voir le tableau personnalisé</Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                  <img
                    src="/site-assets/gift-unwrapping.jpg"
                    alt="Tableau familial en aquarelle déballé comme cadeau personnalisé"
                    width={800}
                    height={533}
                    className="mb-6 aspect-[3/2] w-full rounded-[1.5rem] object-cover"
                  />
                  <Gift className="mb-5 h-10 w-10 text-primary" />
                  <h2 className="font-display text-4xl leading-none text-foreground">Le bon cadeau commence par la bonne photo.</h2>
                  <div className="mt-6 space-y-4">
                    {photoChecklist.map((tip) => (
                      <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Orientation rapide</p>
                <h2 className="section-title text-foreground">Quel chemin choisir selon le destinataire ?</h2>
                <p className="mt-4 text-lg leading-8 text-foreground/65">
                  Un visiteur venu chercher une idée cadeau doit pouvoir se reconnaître vite : chaque piste relie une personne, une photo conseillée, un style probable et un démarrage de création mesurable.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {recipientRoutes.map((route) => (
                  <article key={route.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                    <h3 className="font-display text-3xl leading-none text-foreground">{route.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65">{route.context}</p>
                    <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style conseillé :</span> {route.style}</p>
                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                      <Button variant="premium" className="rounded-full" asChild>
                        <Link to={createPath(route.source)} onClick={() => trackGiftCta(route.source)}>
                          Créer pour ce cadeau
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                        <Link to={route.link}>Lire le guide</Link>
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Par occasion</p>
                <h2 className="section-title text-foreground">Quelle idée cadeau photo selon la personne ?</h2>
                <p className="mt-4 text-lg leading-8 text-foreground/65">
                  Le meilleur cadeau personnalisé n’est pas forcément le plus compliqué : c’est celui qui relie une occasion, une personne et une image forte.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {occasions.map((item) => (
                  <article key={item.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-3xl leading-none text-foreground">{item.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-foreground/65">{item.text}</p>
                    <Link to={item.to} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      {item.cta}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div className="max-w-3xl">
                  <p className="eyebrow mb-3">Idées concrètes</p>
                  <h2 className="section-title text-foreground">6 cadeaux photo personnalisés à créer facilement.</h2>
                </div>
                <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                  <Link to={createPath('ideas_section')} onClick={() => trackGiftCta('ideas_section')}>Tester avec ma photo</Link>
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {giftIdeas.map((idea) => (
                  <Link key={idea.title} to={idea.to} className="group rounded-[1.5rem] border border-white/65 bg-white/65 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <Palette className="mb-4 h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground group-hover:text-primary">{idea.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-foreground/58">Idéal pour : {idea.bestFor}</p>
                    <span className="mt-4 inline-flex text-sm font-medium text-primary">Style conseillé : {idea.style}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="rounded-[2.2rem] border border-white/65 bg-[linear-gradient(180deg,rgba(255,255,255,0.82),rgba(247,239,233,0.94))] p-8 shadow-card md:p-10">
                <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                  <div>
                    <Sparkles className="mb-5 h-8 w-8 text-primary" />
                    <h2 className="section-title text-foreground">Un cadeau utile pour l’émotion, pas pour le placard.</h2>
                  </div>
                  <div className="space-y-4 text-sm leading-7 text-foreground/68">
                    <p>
                      Un tableau photo personnalisé évite le cadeau générique : il utilise une image que le destinataire reconnaît, transforme le souvenir en objet décoratif, et garde une présence dans la maison.
                    </p>
                    <p>
                      Pour rester simple, choisissez d’abord le contexte du cadeau, puis la photo la plus lisible. Le style artistique vient ensuite renforcer l’ambiance : doux pour l’aquarelle, premium pour l’huile, graphique pour le pop art ou sobre pour le croquis.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-shell py-20">
            <div className="container mx-auto px-6">
              <div className="mb-10 max-w-3xl">
                <p className="eyebrow mb-3">Guides liés</p>
                <h2 className="section-title text-foreground">Approfondir avant de commander.</h2>
              </div>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {relatedPages.map((page) => (
                  <Link key={page.to} to={page.to} className="group rounded-[1.6rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                    <h3 className="font-semibold text-foreground group-hover:text-primary">{page.label}</h3>
                    <p className="mt-3 text-sm leading-7 text-foreground/60">{page.helper}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                      Lire le guide
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-shell pb-24 pt-10">
            <div className="container mx-auto px-6 text-center">
              <div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10">
                <Baby className="mx-auto mb-5 h-8 w-8 text-primary" />
                <h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à créer un cadeau photo unique ?</h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">
                  Importez une photo, comparez les styles artistiques, puis avancez vers la commande lorsque le rendu vous convient.
                </p>
                <Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild>
                  <Link to={createPath('final_cta')} onClick={() => trackGiftCta('final_cta')}>
                    Commencer mon cadeau personnalisé
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default IdeesCadeauxPhotoPersonnalises;
